import { CircuitLabelsModel, type CircuitLabelsDto } from '@/models/CircuitLabelsModel';
import { makeAutoObservable } from 'mobx';
import type {
  ICircuitModel,
  IChipInstanceModel,
  ITileManagerModel,
  IConnectionModel,
} from '@/interfaces/models';
import { CircuitId, createCircuitId } from '@/types/ids';
import { CircuitDtoSchema } from '@/schemas/circuit';
import { CircuitType } from '@/schemas/common';
import type { TileManagerDto } from '@/models/TileManagerModel';
import type { ConnectionGraphDto } from '@/models/connections/ConnectionGraph';

export interface CircuitDto {
  labels?: CircuitLabelsDto;
  id: CircuitId;
  circuitType: CircuitType;
  tileManagerDto: TileManagerDto;
  connectionGraphDto?: ConnectionGraphDto;
}

export class CircuitModel implements ICircuitModel {
  id: CircuitId;
  public circuitType: CircuitType;

  tileManagerModel: ITileManagerModel;
  connectionModel: IConnectionModel;

  chips: IChipInstanceModel[] = [];
  labels = new CircuitLabelsModel();

  static fromDto(
    dto: CircuitDto,
    {
      tileManagerModel,
      connectionModel,
      reuseId = true,
    }: {
      tileManagerModel: ITileManagerModel;
      connectionModel: IConnectionModel;
      reuseId?: boolean;
    },
  ) {
    // Tiles were validated individually by TileManagerModel during hydration.
    // Re-parsing every tile here doubled the largest chip's main-thread work.
    CircuitDtoSchema.parse({
      id: dto.id,
      circuitType: dto.circuitType,
      labels: dto.labels,
      tileManagerDto: { tiles: [] },
    });
    const model = new CircuitModel({
      id: reuseId ? dto.id : undefined,
      circuitType: dto.circuitType,
      tileManagerModel,
      connectionModel,
    });

    if (dto.labels) model.labels.load(dto.labels);

    if (!reuseId) {
      model.id = createCircuitId();
    }

    return model;
  }

  constructor({
    id,
    circuitType,
    tileManagerModel,
    connectionModel,
  }: {
    id?: CircuitId;
    circuitType: CircuitType;
    tileManagerModel: ITileManagerModel;
    connectionModel: IConnectionModel;
  }) {
    this.id = id ?? createCircuitId();
    this.circuitType = circuitType;
    this.tileManagerModel = tileManagerModel;
    this.connectionModel = connectionModel;
    makeAutoObservable(this, { toDtoSteps: false }, { autoBind: true });
  }

  get tiles() {
    return this.tileManagerModel.tiles;
  }

  get dto() {
    return {
      ...(this.labels.tags.length ? { labels: this.labels.dto } : {}),
      id: this.id,
      circuitType: this.circuitType,
      tileManagerDto: this.tileManagerModel.dto,
      connectionGraphDto: this.connectionModel.dto,
    };
  }

  *toDtoSteps(): Generator<void, CircuitDto> {
    const tileManagerDto = this.tileManagerModel.toDtoSteps
      ? yield* this.tileManagerModel.toDtoSteps()
      : this.tileManagerModel.dto;
    return {
      ...(this.labels.tags.length ? { labels: this.labels.dto } : {}),
      id: this.id,
      circuitType: this.circuitType,
      tileManagerDto,
      connectionGraphDto: this.connectionModel.dto,
    };
  }
}
